import React, { useMemo, useState } from 'react';

import { FeeStratetySelectorModal } from 'QuoteApp/screens/Purchase';
import { Asset } from 'Shared/components';
import {
  getPaymentItemEntry,
  getPaymentScheduleEntry,
  isInstallmentFee,
} from 'QuoteApp/utils/feeStrategy';
import { formatPrice } from 'Shared/utils';
import { getSelectedPaymentPlan } from 'QuoteApp/utils/paymentPlan';

import { SectionWithTitle } from './base/Section';
import { InfoRow } from './base/InfoRow';
import { InfoHeaderRow } from './base/InfoHeaderRow';
import { FeeBreakdownSectionProps } from './types';
import {
  buildPredictableFeeLines,
  buildPredictableStrategyOption,
  buildVariableFeeLines,
  buildVariableStrategyOption,
  getFeesByStrategyName,
  getFeeValueByName,
  getPaymentPlanByStrategyName,
  getPaymentPlansByFrequency,
} from './utils';
import styles from './styles.module.css';

const buildDataTestId = (label: string, prefix: string) =>
  `${prefix}-${label.toLowerCase().replace(/\s+/g, '-')}`;

export const FeeBreakdownSection: React.FC<FeeBreakdownSectionProps> = ({
  rate,
  downPaymentFeeLines,
  installmentFeeLines,
  feeStrategy,
  isLoading,
  isPayInFullPlan,
  hasSupportForPredictableFee,
  onStrategySelect,
}) => {
  const [openFeeStrategyModal, setOpenFeeStrategyModal] = useState(false);

  const selectedPaymentPlan = useMemo(
    () => getSelectedPaymentPlan(rate)!,
    [rate]
  );

  // In rate.paymentDetails.attributes.paymentPlans there is one plan per available frequency
  // (weekly, bi-weekly, monthly, etc.), but the selected frequency includes two:
  // one with 'variable' and one with 'predictable' feeStrategy.
  // Therefore, the information displayed in this component must be based on the selected frequency.
  const paymentPlansBySelectedFrequency = useMemo(
    () =>
      getPaymentPlansByFrequency(
        rate.paymentDetails.attributes.paymentPlans,
        selectedPaymentPlan.paymentFrequencyIdentifier
      ),
    [rate, selectedPaymentPlan]
  );

  const paymentPlanWithPredictableStrategy = useMemo(
    () =>
      getPaymentPlanByStrategyName(
        paymentPlansBySelectedFrequency,
        'predictable'
      )!,
    [paymentPlansBySelectedFrequency]
  );

  const paymentPlanWithVariableStrategy = useMemo(
    () =>
      getPaymentPlanByStrategyName(
        paymentPlansBySelectedFrequency,
        'variable'
      )!,
    [paymentPlansBySelectedFrequency]
  );

  const installmentEntry = getPaymentScheduleEntry(
    paymentPlanWithPredictableStrategy.paymentSchedule,
    'INSTALLMENT'
  );

  const installmentPredictableProtectionFee = getPaymentItemEntry(
    installmentEntry?.items || [],
    'predictableProtection'
  );

  const variableFees = useMemo(
    () => getFeesByStrategyName(paymentPlansBySelectedFrequency, 'variable'),
    [paymentPlansBySelectedFrequency]
  );
  const variableFeeLines = useMemo(
    () => buildVariableFeeLines(variableFees, isPayInFullPlan),
    [variableFees, isPayInFullPlan]
  );

  const predictableFees = useMemo(
    () => getFeesByStrategyName(paymentPlansBySelectedFrequency, 'predictable'),
    [paymentPlansBySelectedFrequency]
  );
  const predictableFeeLines = useMemo(
    () =>
      buildPredictableFeeLines(predictableFees, variableFees, isPayInFullPlan),
    [predictableFees, variableFees, isPayInFullPlan]
  );

  const feeInterestRate = useMemo(
    () => getFeeValueByName(variableFees, 'installment'),
    [variableFees]
  );

  const isSimpleInterestInstallment = useMemo(
    () =>
      variableFees.find((fee) => isInstallmentFee(fee.name))?.charge.type ===
      'PERCENT',
    [variableFees]
  );

  const renderTitleAction = () => (
    <button
      type="button"
      className={styles.titleAction}
      onClick={() => setOpenFeeStrategyModal(true)}
    >
      <Asset variant="settings" />
      Fees
    </button>
  );

  const renderInstallmentRows = () => {
    return (
      <>
        {downPaymentFeeLines.length > 0 && (
          <>
            <InfoHeaderRow
              key="downpayment-header"
              label="Included in down payment:"
              as="h4"
            />
            {downPaymentFeeLines.map((fee) => (
              <InfoRow
                key={buildDataTestId(fee.label, 'downpayment')}
                data-testid={buildDataTestId(fee.label, 'downpayment')}
                label={fee.label}
                value={fee.amount}
              />
            ))}
          </>
        )}

        {installmentFeeLines.length > 0 && (
          <>
            <InfoHeaderRow
              key="installment-header"
              label="Included in installments:"
              as="h4"
            />
            {installmentFeeLines.map((fee) => (
              <InfoRow
                key={buildDataTestId(fee.label, 'installment')}
                data-testid={buildDataTestId(fee.label, 'installment')}
                label={fee.label}
                value={fee.amount}
              />
            ))}
          </>
        )}
      </>
    );
  };

  const renderPayInFullRows = () => (
    <>
      <InfoHeaderRow
        key="pay-in-full-header"
        label="Included in down payment:"
        as="h4"
      />
      {downPaymentFeeLines.map((fee) => (
        <InfoRow
          key={buildDataTestId(fee.label, 'inFull')}
          data-testid={buildDataTestId(fee.label, 'inFull')}
          label={fee.label}
          value={fee.amount}
        />
      ))}
    </>
  );

  return (
    <SectionWithTitle
      title="Fee breakdown"
      titleAction={
        hasSupportForPredictableFee ? renderTitleAction() : undefined
      }
    >
      {isPayInFullPlan ? renderPayInFullRows() : renderInstallmentRows()}

      <FeeStratetySelectorModal
        title="Choose a fee strategy for this policy"
        showModal={openFeeStrategyModal}
        closeModal={() => setOpenFeeStrategyModal(false)}
        selectedStrategy={feeStrategy}
        isLoading={isLoading}
        onStrategySelect={onStrategySelect}
        strategies={[
          buildPredictableStrategyOption(
            predictableFeeLines,
            installmentPredictableProtectionFee?.amount
              ? formatPrice(installmentPredictableProtectionFee.amount)
              : '',
            isPayInFullPlan
          ),
          buildVariableStrategyOption(
            variableFeeLines,
            feeInterestRate,
            isPayInFullPlan,
            paymentPlanWithVariableStrategy.paymentSchedule,
            isSimpleInterestInstallment
          ),
        ]}
      />
    </SectionWithTitle>
  );
};
